/**
 * Controller for the account administration section
 */
define([
  "backbone",
  "marionette",
  "messaging/messenger",
  "configuration/properties",
  "./accountAdminView",
  "modules/shared/adminMenu/adminMenuModule",
  "modules/header/userMenu/userMenuModule",
  "./engineAdmin/engineAdminModule",
  "./landlordAdmin/landlordAdminModule",
  "./landlordProfile/landlordProfileModule",
  "./tenantAdmin/tenantAdminModule",
  "./kbAdmin/kbAdminModule",
  "./kbProfile/kbProfileModule",
  "./accountAdminModel",
  "./tenantProfile/tenantProfileModule",
], function (
  Backbone,
  Marionette,
  Messenger,
  Properties,
  AccountAdminView,
  AdminMenuModule,
  UserMenuModule,
  EngineAdminModule,
  LandlordAdminModule,
  LandlordProfileModule,
  TenantAdminModule,
  KbAdminModule,
  KbProfileModule,
  AccountAdminModel,
  TenantProfileModule,
) {
  const AccountAdminController = Marionette.Controller.extend({
    initialize: function (options) {
      this.name = "accountAdmin";
      this.region = options.region;
      this._setUpVents();
      this.showSlideout = true;
      this.router = new Router({
        controller: this,
      });
    },
    show: function (options) {
      const user = Messenger.reqres.request("user"); //get user model
      user.applicationName = Properties.applicationName;
      document.title = "Account Administration - " + Properties.applicationName;
      this.layout = new AccountAdminView.accountAdminLayout({ model: user });

      Messenger.onSave(function () {
        Messenger.saveAttempted(true);
      }, this);

      this.sideMenu = this.addSubModule(new AdminMenuModule({ region: this.layout.sideMenu }));
      this.userMenu = this.addSubModule(
        new UserMenuModule({ region: this.layout.userMenu, showAdmin: false }),
      );
      this.engines = this.addSubModule(new EngineAdminModule({ region: this.layout.accountAdmin }));
      if (Properties.showLandlords) {
        this.landlordAdmin = this.addSubModule(
          new LandlordAdminModule({ region: this.layout.accountAdmin }),
        );
        this.landlordProfile = this.addSubModule(
          new LandlordProfileModule({ region: this.layout.accountAdmin }),
        );
      }
      this.tenants = this.addSubModule(new TenantAdminModule({ region: this.layout.accountAdmin }));
      this.tenantProfile = this.addSubModule(
        new TenantProfileModule({ region: this.layout.accountAdmin }),
      );
      this.kbs = this.addSubModule(new KbAdminModule({ region: this.layout.accountAdmin }));
      this.kbProfile = this.addSubModule(
        new KbProfileModule({ region: this.layout.accountAdmin, secretKeyExists: true }),
      );

      this.layout.on(
        "show",
        function () {
          if (this.showSlideout) {
            this.layout.showSlideout();
          }
          this._showSubViews(options);
        },
        this,
      );
      this.layout.on(
        "toggle:slideout",
        function () {
          this.showSlideout = !this.showSlideout;
          if (this.showSlideout) {
            this.layout.showSlideout();
          } else {
            this.layout.hideSlideout();
          }
        },
        this,
      );

      this.showRegion(this.layout);
    },

    getSubsectionFromUserProfile: function () {
      const userIsTenantAdmin = Messenger.reqres.request("user:is:admin");
      const userIsGlobalAdmin = Messenger.reqres.request("user:is:global:admin");
      // TODO - Landlord -> different default page
      if (userIsGlobalAdmin) {
        return "engines";
      } else if (userIsTenantAdmin) {
        return "tenants";
      } else {
        return "";
      }
    },

    _showSubViews: function (options) {
      const subsection = options.subsection || this.getSubsectionFromUserProfile();
      if (!subsection) {
        return;
      }
      const menuItems = Messenger.reqres.request("account:admin:menu:items");
      menuItems.findWhere({ name: subsection }).set("default", true);
      const menuStyle = Messenger.reqres.request("account:admin:menu:style");
      this.sideMenu.show(menuStyle, menuItems, "account:admin", subsection);
      this.userMenu.show();
      switch (subsection) {
        case "engines":
          this.engines.show(options);
          break;
        case "kbs":
          this.kbs.show(options);
          break;
        case "landlords":
          if (options.landlord || options.landlordId) {
            this.landlordProfile.show(options);
          } else {
            this.landlordAdmin.show(options);
          }
          break;
        case "tenants":
          if (options.tenant || options.tenantId) {
            this.tenantProfile.show(options);
          } else {
            this.tenants.show(options);
          }
          break;
      }
    },

    _routeDefault: function () {
      // If user isn't admin, kick 'em out!
      const isAdmin = Messenger.reqres.request("user:is:admin");
      if (!isAdmin) {
        Backbone.history.navigate("", { trigger: true });
        return;
      }
      const subsection = this.getSubsectionFromUserProfile();
      Backbone.history.navigate("accountAdmin/" + subsection);
      this.show({ subsection: subsection });
    },
    _routeEngines: function (engineId) {
      const isGlobalAdmin = Messenger.reqres.request("user:is:global:admin");
      if (!isGlobalAdmin) {
        this._routeDefault();
      } else {
        Messenger.vent.trigger("toggle:header", false);
        this.show({
          subsection: "engines",
          engineId: engineId,
        });
      }
    },
    _routeKbs: function (kbId) {
      const isGlobalAdmin = Messenger.reqres.request("user:is:global:admin");
      // TODO: Determine if the user is a landlord admin
      const isLandlordAdmin = false;
      const isTenantAdmin = Messenger.reqres.request("user:is:admin");
      if (!isGlobalAdmin && !isLandlordAdmin && !isTenantAdmin) {
        this._routeDefault();
      } else {
        Messenger.vent.trigger("toggle:header", false);
        this.show({
          subsection: "kbs",
          kbId: kbId,
        });
      }
    },
    _routeLandlords: function (landlordId) {
      const isGlobalAdmin = Messenger.reqres.request("user:is:global:admin");
      // TODO: Determine if the user is a landlord admin
      const isLandlordAdmin = false;
      if ((!isGlobalAdmin && !isLandlordAdmin) || !Properties.showLandlords) {
        this._routeDefault();
      } else {
        Messenger.vent.trigger("toggle:header", false);
        this.show({
          subsection: "landlords",
          landlordId: landlordId,
        });
      }
    },
    _routeTenants: function (tenantId, submodelType, submodelId) {
      const isGlobalAdmin = Messenger.reqres.request("user:is:global:admin");
      // TODO: Determine if the user is a landlord admin
      const isLandlordAdmin = false;
      const isTenantAdmin = Messenger.reqres.request("user:is:admin");
      if (!isGlobalAdmin && !isLandlordAdmin && !isTenantAdmin) {
        this._routeDefault();
      } else {
        Messenger.vent.trigger("toggle:header", false);
        this.show({
          subsection: "tenants",
          tenantId: tenantId,
          submodelType: submodelType,
          submodelId: submodelId,
        });
      }
    },
    _setUpVents: function () {
      Messenger.vent.on(
        "show:kb:profile",
        function (options) {
          this.kbProfile.show(options);
        },
        this,
      );
      Messenger.vent.on(
        "show:account:admin:authoring",
        function () {
          const kbId = Messenger.reqres.request("active:kb");
          Backbone.history.navigate("", { trigger: true });
          Messenger.vent.trigger("toggle:header", true);
          Messenger.command.execute("set:active:kb", kbId);
        },
        this,
      );
    },
  });

  const Router = Marionette.AppRouter.extend({
    appRoutes: {
      accountAdmin: "_routeDefault",
      "accountAdmin/engines(/:engineId)": "_routeEngines",
      "accountAdmin/landlords(/:landlordId)": "_routeLandlords",
      "accountAdmin/tenants(/:tenantId)(/:submodelType)(/:submodelId)": "_routeTenants",
      "accountAdmin/kbs(/:kbId)": "_routeKbs",
    },
  });

  return AccountAdminController;
});
